import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import styled from 'styled-components';
import { IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import {
  FlipToFrontOutlined,
  MoreHoriz,
  SaveAlt,
  Schedule,
  VisibilityOutlined,
} from '@material-ui/icons';
import { fullloader } from '../../../../redux/action';
import { getMsgBy_Id } from '../../../../redux/services';
import {
  selectedOrganizationSelector,
  userDetailSelector,
  userPrivilegesSelector,
} from '../../../../redux/selector';
import SmartResendDrawer from '../../../../Component/Analytics/Components/SmartResendDrawer';
import { addStatusesToMessage } from '../../../../Component/Analytics/utils';
import {
  duplicateToDraft,
  exportMessageDesign,
  viewMessage,
} from '../../actions';
import PlaneIcon from './PlaneIcon';
import { BLUE, BORDER } from '../../AnalyticsV2.styled';

const MenuButton = styled(IconButton)`
  &.MuiIconButton-root {
    border: 1px solid ${BORDER};
    border-radius: 4px;
    padding: 4px;
  }
`;

const menuPaperStyle = {
  marginTop: 6,
  minWidth: 260,
  border: `1px solid ${BORDER}`,
  borderRadius: 12,
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
};

const Item = styled(MenuItem)`
  &.MuiMenuItem-root {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 24px;
    font-size: 17px;
    color: #1c1c1c;
    white-space: normal;
  }
  &.MuiMenuItem-root:hover {
    background: #f6f6f6;
  }
`;

const ItemLabel = styled.span`
  max-width: 190px;
  line-height: 1.2;
`;

// Looks disabled but still receives hover, so its tooltip can show the
// reason (a MUI disabled item ignores the mouse). Clicking it does nothing.
const BlockedItem = styled(Item)`
  &.MuiMenuItem-root {
    opacity: 0.5;
    cursor: default;
  }
  &.MuiMenuItem-root:hover {
    background: transparent;
  }
`;

const iconStyle = { color: BLUE, fontSize: 22 };

// Why a resend to non-openers can't be scheduled, or null when it can.
// Same rules as MessageDetails.js: once per message, within 10 days of
// sending; the texts for an existing resend also come from there.
const getResendBlockedReason = message => {
  if (message.smartResendScheduleTime) {
    return message.status === 'cancelled'
      ? 'Cancelled resend'
      : `Resent on ${moment
          .unix(message.smartResendScheduleTime)
          .format('MM/DD/YY')}`;
  }
  if (message.status === 'cancelled') return 'Message was cancelled';
  if (message.status !== 'sent') return 'Available once the message is sent';
  if (moment.unix(message.sendTime).isBefore(moment().subtract(10, 'days'))) {
    return 'Allowed only within 10 days of sending';
  }
  return null;
};

const RowMenu = ({ message, onChanged }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const organization = useSelector(selectedOrganizationSelector);
  const user = useSelector(userDetailSelector);
  const { isSU } = useSelector(userPrivilegesSelector);
  const [anchor, setAnchor] = useState(null);
  const [resendMessage, setResendMessage] = useState(null);
  const resendBlockedReason = getResendBlockedReason(message);

  // The actions need the full message (body, design, versionB).
  const runWithFullMessage = action => async () => {
    setAnchor(null);
    dispatch(fullloader(true, 'Loading message...'));
    const fullMessage = await getMsgBy_Id(message._id, true);
    dispatch(fullloader(false));
    await action(fullMessage);
  };

  const handleDuplicate = async fullMessage => {
    dispatch(fullloader(true, 'Loading edited message...'));
    const draftId = await duplicateToDraft(fullMessage, {
      userId: user.id,
      organizationId: organization.id,
    });
    dispatch(fullloader(false));
    history.push(`/dashboard/sendmessage?draft=${draftId}`);
  };

  return (
    <>
      <MenuButton
        size="small"
        onClick={event => setAnchor(event.currentTarget)}
      >
        <MoreHoriz fontSize="small" />
      </MenuButton>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        getContentAnchorEl={null}
        PaperProps={{ style: menuPaperStyle }}
        MenuListProps={{ disablePadding: true }}
      >
        <Item onClick={runWithFullMessage(viewMessage)}>
          <VisibilityOutlined style={iconStyle} />
          <ItemLabel>View</ItemLabel>
        </Item>
        <Item onClick={runWithFullMessage(handleDuplicate)}>
          <FlipToFrontOutlined style={iconStyle} />
          <ItemLabel>Duplicate to draft</ItemLabel>
        </Item>
        {resendBlockedReason ? (
          <Tooltip title={resendBlockedReason} placement="left">
            <BlockedItem aria-disabled="true">
              <PlaneIcon color={BLUE} Badge={Schedule} />
              <ItemLabel>Schedule resend to non-openers</ItemLabel>
            </BlockedItem>
          </Tooltip>
        ) : (
          <Item onClick={runWithFullMessage(setResendMessage)}>
            <PlaneIcon color={BLUE} Badge={Schedule} />
            <ItemLabel>Schedule resend to non-openers</ItemLabel>
          </Item>
        )}
        {isSU && (
          <Item onClick={runWithFullMessage(exportMessageDesign)}>
            <SaveAlt style={iconStyle} />
            <ItemLabel>Export HTML/JSON</ItemLabel>
          </Item>
        )}
      </Menu>
      {resendMessage && (
        <SmartResendDrawer
          isDrawerVisible
          setDrawerdVisibility={() => setResendMessage(null)}
          message={resendMessage}
          setSelectedMessage={() => onChanged()}
          addStatusesToMessage={addStatusesToMessage}
        />
      )}
    </>
  );
};

export default RowMenu;
